import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { Calendar } from "primereact/calendar";
import { FilterMatchMode } from "primereact/api";
import { useSelector } from "react-redux";
import { useLanguage } from "../../../../contexts/LanguageContext";
import { selectUserParams } from "../../../../store/slices/userParamsSlice";

const TEXT_FILTER_FIELDS = ["vbeln", "dmsOrderNo", "chassisNo", "kunnr", "customerInfo", "siparisiOlusturanBayi", "bstkd", "lprio", "matnr", "maktx", "meins", "waerk", "zterm"];
const NUMBER_FILTER_FIELDS = ["kwmeng", "bakiyeMktr", "netwr", "birimTutar"];
const DATE_FILTER_FIELDS = ["erdat", "edatu"];

const TEXT_MATCH_MODES = [FilterMatchMode.CONTAINS, FilterMatchMode.STARTS_WITH, FilterMatchMode.ENDS_WITH, FilterMatchMode.EQUALS, FilterMatchMode.NOT_CONTAINS, FilterMatchMode.NOT_EQUALS];
const NUMBER_MATCH_MODES = [FilterMatchMode.CONTAINS, FilterMatchMode.EQUALS, FilterMatchMode.NOT_EQUALS];

const MATCH_MODE_LABEL_KEYS = {
  [FilterMatchMode.CONTAINS]: "filter.contains",
  [FilterMatchMode.NOT_CONTAINS]: "filter.notContains",
  [FilterMatchMode.STARTS_WITH]: "filter.startsWith",
  [FilterMatchMode.ENDS_WITH]: "filter.endsWith",
  [FilterMatchMode.EQUALS]: "filter.equals",
  [FilterMatchMode.NOT_EQUALS]: "filter.notEquals"
};

const DEFAULT_TEXT_MATCH_MODE = FilterMatchMode.CONTAINS;
const DEFAULT_NUMBER_MATCH_MODE = FilterMatchMode.CONTAINS;
const DEFAULT_DATE_MATCH_MODE = FilterMatchMode.DATE_IS;

const FILTER_DELAY = 400;

const DebouncedFilterInput = ({ value, placeholder, numeric, onApply }) => {
  const [draft, setDraft] = useState(value ?? "");
  const draftRef = useRef(value ?? "");
  const timerRef = useRef(null);

  useEffect(() => {
    const next = value ?? "";
    if (next !== draftRef.current) {
      clearTimeout(timerRef.current);
      draftRef.current = next;
      setDraft(next);
    }
  }, [value]);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  const apply = (next) => {
    clearTimeout(timerRef.current);
    if (next === (value ?? "")) return;
    onApply(next === "" ? null : next);
  };

  const handleChange = (e) => {
    const next = numeric ? e.target.value.replace(/[^\d.,-]/g, "") : e.target.value;
    draftRef.current = next;
    setDraft(next);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => apply(next), FILTER_DELAY);
  };

  return (
    <InputText
      value={draft}
      onChange={handleChange}
      onBlur={() => apply(draftRef.current)}
      onKeyDown={(e) => e.key === "Enter" && apply(draftRef.current)}
      placeholder={placeholder}
      className="p-column-filter"
      style={{ minWidth: numeric ? "8rem" : "10rem" }}
      inputMode={numeric ? "decimal" : undefined}
    />
  );
};

const OrderListTable = ({ data, totalRecords, isLoading, onPage, onSort, lazyParams, sortField, sortOrder, columnFilters, onColumnFiltersChange, filterResetToken = 0 }) => {
  const { th } = useLanguage();
  const userParams = useSelector(selectUserParams);
  const isInterservice = userParams?.isInterservice === true;
  const safe = lazyParams || { page: 1, rows: 10 };

  const handlePage = (e) => onPage?.(e);
  const handleSort = (e) => onSort?.(e);

  const text  = (v) => (v == null || v === "" ? "-" : v);
  const num   = (v) => (v == null ? "-" : v);
  const date  = (v) => (v ? new Date(v).toLocaleDateString() : "-");
  const money = (row, amountField, currField) => {
    const amt = row?.[amountField], ccy = row?.[currField] ?? "";
    if (amt == null) return "-";
    return `${Number(amt).toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})} ${ccy}`;
  };

  const textMatchModeOptions = useMemo(() => TEXT_MATCH_MODES.map((matchMode) => ({ label: th(MATCH_MODE_LABEL_KEYS[matchMode]), value: matchMode })), [th]);
  const numberMatchModeOptions = useMemo(() => NUMBER_MATCH_MODES.map((matchMode) => ({ label: th(MATCH_MODE_LABEL_KEYS[matchMode]), value: matchMode })), [th]);

  const inputFilterElement = (options, numeric) => (
    <DebouncedFilterInput value={options.value} numeric={numeric} placeholder={th("common.search")} onApply={(next) => options.filterApplyCallback(next)} />
  );

  const dateFilterElement = (options) => {
    const handleDateChange = (e) => {
      let normalized = e.value;
      if (normalized instanceof Date) {
        normalized = new Date(normalized.getFullYear(), normalized.getMonth(), normalized.getDate());
      }
      options.filterApplyCallback(normalized);
    };

    return <Calendar value={options.value} onChange={handleDateChange} dateFormat="dd.mm.yy" placeholder={th("common.search")} showIcon showButtonBar className="p-column-filter" style={{ minWidth: "11rem" }} />;
  };

  const textFilter = {
    filter: true,
    showFilterMenu: true,
    showClearButton: false,
    filterMatchModeOptions: textMatchModeOptions,
    filterElement: (options) => inputFilterElement(options, false)
  };

  const numberFilter = {
    filter: true,
    showFilterMenu: true,
    showClearButton: false,
    filterMatchModeOptions: numberMatchModeOptions,
    filterElement: (options) => inputFilterElement(options, true)
  };

  const dateFilter = {
    filter: true,
    showFilterMenu: false,
    showClearButton: false,
    filterElement: dateFilterElement
  };

  const tableFilters = useMemo(() => {
    const result = {};
    const add = (fields, defaultMatchMode) => {
      fields.forEach((field) => {
        const model = columnFilters?.[field];
        result[field] = { value: model?.value ?? null, matchMode: model?.matchMode ?? defaultMatchMode };
      });
    };
    add(TEXT_FILTER_FIELDS, DEFAULT_TEXT_MATCH_MODE);
    add(NUMBER_FILTER_FIELDS, DEFAULT_NUMBER_MATCH_MODE);
    add(DATE_FILTER_FIELDS, DEFAULT_DATE_MATCH_MODE);
    return result;
  }, [columnFilters]);

  const handleFilter = useCallback(
    (e) => {
      const incoming = e?.filters || {};
      const updated = { ...(columnFilters || {}) };
      const collect = (fields, defaultMatchMode, numeric) => {
        fields.forEach((field) => {
          if (!(field in incoming)) return;
          updated[field] = { value: incoming[field]?.value ?? null, matchMode: incoming[field]?.matchMode ?? defaultMatchMode, numeric };
        });
      };
      collect(TEXT_FILTER_FIELDS, DEFAULT_TEXT_MATCH_MODE, false);
      collect(NUMBER_FILTER_FIELDS, DEFAULT_NUMBER_MATCH_MODE, true);
      collect(DATE_FILTER_FIELDS, DEFAULT_DATE_MATCH_MODE, false);

      onColumnFiltersChange?.(updated, filterResetToken);
    },
    [columnFilters, onColumnFiltersChange, filterResetToken]
  );

  return (
    <DataTable
      value={data}
      loading={isLoading}
      paginator
      lazy
      first={(safe.page - 1) * safe.rows}
      rows={safe.rows}
      totalRecords={totalRecords}
      onPage={handlePage}
      rowsPerPageOptions={[10, 20, 50]}
      sortField={sortField}
      sortOrder={sortOrder}
      onSort={handleSort}
      sortMode="single"
      filterDisplay="row"
      filters={tableFilters}
      onFilter={handleFilter}
      showGridlines
      stripedRows
      responsiveLayout="scroll"
      emptyMessage={th("order.emptyMessage") || "No records found."}
      removableSort
    >
      <Column field="vbeln" header={th("order.sdNumber")} sortable body={(r) => text(r.vbeln)} {...textFilter} />
        <Column field="dmsOrderNo" header={th("order.dmsOrderNo")} sortable body={(r) => text(r.dmsOrderNo)} {...textFilter} />
        <Column field="chassisNo" header={th("order.chassisNo")} sortable body={(r) => text(r.chassisNo)} {...textFilter} />
        <Column field="kunnr" header={th("order.customerNo")} sortable body={(r) => text(r.kunnr)} {...textFilter} />
        <Column field="customerInfo" header={th("order.customer")} sortable body={(r) => text(r.customerInfo)} {...textFilter} />
        {isInterservice && (
          <Column field="siparisiOlusturanBayi" header={th("shipmentDelivery.orderCreatingDealer")} body={(r) => text(r.siparisiOlusturanBayi)} {...textFilter} />
        )}
        <Column field="bstkd" header={th("order.customerRef")} sortable body={(r) => text(r.bstkd)} {...textFilter} />
        <Column field="erdat" header={th("order.createdOn")} sortable body={(r) => date(r.erdat)} {...dateFilter} />
        <Column field="lprio" header={th("order.deliveryPriority")} sortable body={(r) => text(r.lprioDescription)} {...textFilter} />
        <Column field="matnr" header={th("order.materialNo")} sortable body={(r) => text(r.matnr)} {...textFilter} />
        <Column field="maktx" header={th("order.materialDesc")} sortable body={(r) => text(r.maktx)} {...textFilter} />
        <Column field="kwmeng" header={th("order.orderedQty")} sortable body={(r) => num(r.kwmeng)} {...numberFilter} />
        <Column field="meins" header={th("order.uom")} sortable body={(r) => text(r.meins)} {...textFilter} />
        <Column field="bakiyeMktr" header={th("order.remainingQty")} sortable body={(r) => num(r.bakiyeMktr)} {...numberFilter} />
        <Column field="netwr" header={th("order.netAmount")} sortable body={(r) => money(r,"netwr","waerk")} {...numberFilter} />
        <Column field="birimTutar" header={th("order.unitPrice")} sortable body={(r) => money(r,"birimTutar","waerk")} {...numberFilter} />
        <Column field="waerk" header={th("order.currency")} sortable body={(r) => text(r.waerk)} {...textFilter} />
        <Column field="zterm" header={th("order.paymentTerm")} sortable body={(r) => text(r.ztermDescription)} {...textFilter} />
        <Column field="edatu" header={th("order.reqDeliveryDate")} sortable body={(r) => date(r.edatu)} {...dateFilter} />
      </DataTable>
  );
};

export default OrderListTable;
